import React from 'react';
import { Stack, Typography } from '@mui/material';

import { PaperCard } from 'components/Card';
import { FullPageStep } from 'components/Layout';
import { InstrumentCard } from './InstrumentCard';
import { useAuth } from 'lib/auth';

import { useFrameworkAgreements } from 'features/frameworkAgreements/api/getAllFrameworkAgreements';
import { useGetAll as useDPSList } from 'features/dps/api/getAll';
import { useDirects } from 'features/direct/api/getAll';

export const InstrumentGrid = ({ items, logoUrl, logoName, onSelect }) => (
  <Stack direction="column" gap={2}>
    {items
      .reduce((rows, item, index) => {
        if (index % 3 === 0) rows.push([]);
        rows[rows.length - 1].push(item);
        return rows;
      }, [])
      .map((row, rowIndex, allRows) => {
        const emptySlotsNeeded = 3 - row.length;
        return (
          <Stack key={rowIndex} direction={{ xs: 'column', md: 'row' }} gap={2}>
            {row.map((item) => (
              <InstrumentCard
                key={`${item.instrumentType}-${item.id}`}
                instrumentType={item.instrumentType}
                name={item.name}
                refNumber={item.refNumber}
                description={item.description}
                logoUrl={logoUrl}
                logoName={logoName}
                onClick={() => onSelect(item)}
              />
            ))}
            {rowIndex === allRows.length - 1 &&
              emptySlotsNeeded > 0 &&
              Array.from({ length: emptySlotsNeeded }).map((_, index) => (
                <PaperCard
                  sx={{ flex: 1, boxShadow: 0, borderRadius: 1, p: 2, visibility: 'hidden', display: { xs: 'none', md: 'flex' } }}
                  key={`empty-${rowIndex}-${index}`}
                />
              ))}
          </Stack>
        );
      })}
  </Stack>
);

export const InstrumentPicker = ({ onCancel, onNext }) => {
  const { user } = useAuth();
  const { data: { frameworkAgreements } } = useFrameworkAgreements({ limit: 100 });
  const { data: { dpses } } = useDPSList({ limit: 100 });
  const { data: { directs } } = useDirects({ limit: 100 });

  const items = [
    ...(frameworkAgreements || []).map((fa) => ({
      instrumentType: 'fa',
      id: fa.fa_id,
      name: fa.fa_name,
      refNumber: fa.ref_number,
      description: fa.Description,
    })),
    ...(dpses || []).map((dps) => ({
      instrumentType: 'dps',
      id: dps.dps_id,
      name: dps.dps_name,
      refNumber: dps.ref_number,
      description: dps.Description,
    })),
    ...(directs || []).map((direct) => ({
      instrumentType: 'direct',
      id: direct.direct_id,
      name: direct.direct_name,
      refNumber: direct.ref_number,
      description: direct.Description,
    })),
  ];

  const handleSelect = (item) => {
    onNext({ instrumentType: item.instrumentType, instrumentId: item.id, instrumentName: item.name });
  };

  return (
    <FullPageStep onClose={onCancel}>
      <Stack gap={3} px={4} py={3} bgcolor="background.paper" borderTop="1px solid" borderColor="border.main">
        <Typography variant="h3">Select an agreement, DPS, or direct</Typography>
        <Typography>
          Choose which of your procurement instruments this job advertisement belongs to.
        </Typography>
        {items.length ? (
          <InstrumentGrid
            items={items}
            logoUrl={user?.Company?.square_logo_url}
            logoName={user?.Company?.Name}
            onSelect={handleSelect}
          />
        ) : (
          <Typography color="text.secondary">
            You don't have any framework agreements, DPS, or directs yet.
          </Typography>
        )}
      </Stack>
    </FullPageStep>
  );
};
